import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { HiOutlineArrowLeft } from "react-icons/hi";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { RiErrorWarningFill } from "react-icons/ri";
import * as attenadanceService from "../../Services/attendanceService";
import { getAllUsers } from "../../Services/UserService";
import SearchableDropdown from "../../Components/Common/SearchableDropdown";
import * as siteService from "../../Services/siteService";
import { BASE } from "../../routes";
import { Loader } from "../../Components/Common";
import { MESSAGES } from "./constants";
import { GET_ALL_PAGINATION } from "../../Constants/Strings";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";
import { getCurrentTime } from "../../Utils/timeUtils";

const validationSchema = Yup.object({
  user: Yup.string().required(MESSAGES.USER_REQUIRED),
  site_name: Yup.string().required(MESSAGES.SITES_NAME_REQUIRED),
  date: Yup.date()
    .max(new Date(Date.now() - 24 * 60 * 60 * 1000), MESSAGES.FUTURE_DATE_ERROR)
    .required(MESSAGES.DATE_REQUIRED),
  check_in_time: Yup.string().required(MESSAGES.TIME_REQUIRED),
  check_out_time: Yup.string()
    .required(MESSAGES.TIME_REQUIRED)
    .test(
      "check-out-after-check-in",
      "Check-out time must be later than check-in time",
      function (value) {
        const { check_in_time } = this.parent;

        if (check_in_time && value) {
          const checkInTime = new Date(`1970-01-01T${check_in_time}:00`);
          const checkOutTime = new Date(`1970-01-01T${value}:00`);

          return checkOutTime > checkInTime;
        }
        return true;
      }
    ),
});

const BackDated = () => {
  const [siteOptions, setSiteOptions] = useState([]);
  const navigate = useNavigate();
  const [selectedUser, setSelectedUser] = useState(null);
  const [actualAttendanceMessage, setActualAttendanceMessage] = useState("");

  const initialAttendanceValue = {
    user: "",
    site_name: "",
    date: "",
    check_in_time: "",
    check_out_time: "",
  };
  const handleSubmit = async (values) => {
    const formData = new FormData();
    formData.append("user", values.user);
    formData.append("site", values.site_name);
    formData.append("date", values.date);
    formData.append("is_backdated", 1);
    formData.append("check_in_time", values.check_in_time);
    formData.append("check_out_time", values.check_out_time);
    

    try {
      const response = await attenadanceService.backDated(formData);
      if (response.status === 201) {
        toast.success(`Backdated ${MESSAGES.SUBMITTED_ATTENDANCE(getCurrentTime())}`);
        navigate(BASE);
      } else {
        const msg = extractErrorMessage(response) || MESSAGES.FAILED_SUBMISSION;
        toast.error(msg);
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || MESSAGES.FAILED_SUBMISSION;
      toast.error(msg);
    }
  };
  


const fetchUsersForDropdown = async ({ search }) => {
  try {
    const response = await getAllUsers({ ...GET_ALL_PAGINATION, search });
    return response.status === 200 ? response.data?.results || [] : [];
  } catch (error) {
    return [];
  }
};

const getActualAttendanceInfo =  async (userId, date)=>{
  setActualAttendanceMessage("");
  if (!userId || !date) return;
  try {
        const params = {
          date:date ,
          user:userId
        }
        const response = await attenadanceService.getAttendanceList(params);
        if (response.status === 200) {
            const attendanceResult = response.data.results;
            const is_checkedin = attendanceResult.find(x =>  x.checkin_time !== null );
            const is_checkedout = attendanceResult.find(x =>  x.checkout_time !== null );
            const already_checkedin = is_checkedin? is_checkedin.checkin_time:"";
            const already_checkedout = is_checkedout? is_checkedout.checkout_time:"";
            if(already_checkedin !== "" && already_checkedout !== ""){
              const inTime = already_checkedin.slice(0, 5);
              const outTime = already_checkedout.slice(0, 5);
              setActualAttendanceMessage(MESSAGES.ATTENDANCE_ALREADY_EXISTS_RANGE(inTime,outTime))
            }
            else if (already_checkedin !== "" && already_checkedout === ""){
                const inTime = already_checkedin.slice(0,5);
                setActualAttendanceMessage(MESSAGES.ATTENDANCE_ALREADY_EXISTS_CHECKIN(inTime))
              }
        }
    } catch (error) {
        const msg = extractErrorMessage(error) || MESSAGES.REQUEST_ERROR;
    } 
}
  
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await siteService.getSites(GET_ALL_PAGINATION);
        setSiteOptions(response.data.results);
      } catch (error) {
        toast.error(MESSAGES.SITES_ERROR);
      }
    };

    fetchSites();
  }, []);

  return (
    <div>
      <div className="max-w-3xl mx-auto p-4 rounded-lg flex justify-center items-center">
        <div className="bg-white rounded-lg p-4 w-full max-w-xl overflow-y-auto max-h-screen shadow-md">
          <div className="relative flex items-center justify-center mb-4 h-12">
            <button
              type="button"
              onClick={() => navigate(BASE)}
              className="absolute left-0 text-primary hover:text-secondary text-2xl pl-2"
              title="Back"
            >
              <HiOutlineArrowLeft />
            </button>
            <h2 className="text-2xl font-bold text-center text-primary">
              Backdated Attendance
            </h2>
          </div>
          { actualAttendanceMessage && (
              <div className="flex justify-center">
                <div className="mx-2 sm:mx-0 flex items-start gap-2 bg-yellow-100 text-yellow-800 border border-yellow-300 px-4 py-2 rounded-md text-sm font-medium shadow-sm max-w-md w-auto">
                  <RiErrorWarningFill className="w-5 h-5 flex-shrink-0 text-yellow-700 mt-0.5" />
                  <span className="flex-1">
                    { actualAttendanceMessage}
                  </span>
                </div>
              </div>
            )}
          <Formik
            initialValues={initialAttendanceValue}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, values, setFieldValue }) =>
              isSubmitting ? (
                <div className="flex justify-center items-center h-40">
                  <Loader />
                </div>
              ) : (
                <Form className="space-y-4 text-secondary">
                  {/* User Dropdown */}
                  <div>
                    <SearchableDropdown
                      label="User"
                      fetchFunction={fetchUsersForDropdown}
                      valueKey="id"
                      labelKey="full_name"
                      placeholder="Select a user"
                      search_box_placeholder="Search User"
                      value={selectedUser}
                      onSelect={async (selected) => {
                        setSelectedUser(selected);
                        setFieldValue("user", selected?.id || "");
                        await getActualAttendanceInfo(selected?.id, values.date);
                      }}
                    />
                    <ErrorMessage
                      name="user"
                      component="div"
                      className="text-red-600 text-sm"
                    />
                  </div>

                  {/* Site Dropdown */}
                  <div>
                    <label className="block font-medium">Site</label>
                    <Field
                      as="select"
                      name="site_name"
                      className="w-full border border-gray-300 rounded px-3 py-2 mt-1"
                    >
                      <option value="">Select a site</option>
                      {siteOptions.map((site) => (
                        <option key={site.id} value={site.id}>
                          {site.name}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage
                      name="site_name"
                      component="div"
                      className="text-red-600 text-sm"
                    />
                  </div>

                  {/* Date Field */}
                  <div>
                    <label className="block font-medium">Date</label>
                    <Field
                      name="date"
                      type="date"
                      max={new Date().toISOString().split("T")[0]}
                      className="w-full border border-gray-300 rounded px-3 py-2 mt-1"
                      onInput={async(e) => {
                        const value = e.target.value;
                        const year = value.split("-")[0];
                        await getActualAttendanceInfo(values.user, value)
                        if (year.length > 4) {
                          e.target.value = "";
                        }
                      }}
                    />
                    <ErrorMessage
                      name="date"
                      component="div"
                      className="text-red-600 text-sm"
                    />
                  </div>

                  {/* Check-In Time Field */}
                  <div>
                    <label className="block font-medium">Check-In Time</label>
                    <Field
                      name="check_in_time"
                      type="time"
                      className="w-full border border-gray-300 rounded px-3 py-2 mt-1"
                    />
                    <ErrorMessage
                      name="check_in_time"
                      component="div"
                      className="text-red-600 text-sm"
                    />
                  </div>

                  {/* Check-Out Time Field */}
                  <div>
                    <label className="block font-medium">Check-Out Time</label>
                    <Field
                      name="check_out_time"
                      type="time"
                      className="w-full border border-gray-300 rounded px-3 py-2 mt-1"
                    />
                    <ErrorMessage
                      name="check_out_time"
                      component="div"
                      className="text-red-600 text-sm"
                    />
                  </div>

                  {/* Buttons */}
                  <div className="flex justify-between mt-8">
                    <button
                      type="button"
                      onClick={() => navigate(BASE)}
                      className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-primary text-white rounded hover:bg-secondary"
                    >
                      Submit
                    </button>
                  </div>
                </Form>
              )
            }
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default BackDated;
